import { useQuery } from '@tanstack/react-query';
import { orpc } from '@/api/rpc/client';
import { Action, Subject } from '@erp/authz';
import { Checkbox } from '@/components/ui/checkbox';
import { Spinner } from '@/components/ui/spinner';
import { Badge } from '@/components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { XCircleIcon } from '@phosphor-icons/react';
import {
  AgentPermission,
  permissionKey,
  isAllowableAgentPermission,
  ALLOWED_ACTIONS,
} from './types';

export interface AgentPermissionMatrixProps {
  permissions: AgentPermission[];
  onChange: (permissions: AgentPermission[]) => void;
}

const ACTION_LABELS: Record<string, string> = {
  create: 'Create',
  read: 'View',
  update: 'Edit',
  delete: 'Delete',
  approve: 'Approve',
  execute: 'Execute',
  report: 'Report',
  manage: 'Manage',
};

function formatSubject(subject: string): string {
  return subject
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2');
}

export function AgentPermissionMatrix({
  permissions,
  onChange,
}: AgentPermissionMatrixProps) {
  const {
    data: availableData,
    isLoading,
    error,
  } = useQuery(orpc.authz.listAvailablePermissions.queryOptions());

  const permissionKeys = new Set(
    permissions.map((p) => permissionKey(p.action, p.subject)),
  );

  function handleToggle(action: Action, subject: Subject) {
    const key = permissionKey(action, subject);
    const isCurrentlyChecked = permissionKeys.has(key);

    if (isCurrentlyChecked) {
      onChange(
        permissions.filter(
          (p) => !(p.action === action && p.subject === subject),
        ),
      );
    } else if (isAllowableAgentPermission(action, subject)) {
      onChange([...permissions, { action, subject }]);
    }
  }

  function handleToggleRow(subject: Subject, actions: Action[]) {
    const validActions = actions.filter((action) =>
      isAllowableAgentPermission(action, subject),
    );
    const allEnabled = validActions.every((action) =>
      permissionKeys.has(permissionKey(action, subject)),
    );

    if (allEnabled) {
      onChange(permissions.filter((p) => p.subject !== subject));
    } else {
      const existingKeys = new Set(
        permissions
          .filter((p) => p.subject === subject)
          .map((p) => permissionKey(p.action, p.subject)),
      );
      const newPermissions = validActions
        .filter((action) => !existingKeys.has(permissionKey(action, subject)))
        .map((action) => ({ action, subject }));
      onChange([...permissions, ...newPermissions]);
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner className="h-6 w-6" />
        <span className="ml-2 text-muted-foreground">
          Loading permissions...
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-destructive">
        <XCircleIcon className="h-8 w-8 mb-2" />
        <p className="font-medium">Error loading permissions</p>
        <p className="text-sm text-muted-foreground mt-1">
          Please try refreshing the page
        </p>
      </div>
    );
  }

  if (!availableData) {
    return null;
  }

  const { actions, domainGroups, validActionsPerSubject } = availableData;
  const displayActions = actions.filter((a) => ALLOWED_ACTIONS.includes(a));

  function isActionValidForSubject(action: Action, subject: Subject): boolean {
    const validActions = validActionsPerSubject[subject];
    return validActions?.includes(action) ?? false;
  }

  return (
    <div className="flex flex-col">
      <Accordion
        type="multiple"
        defaultValue={domainGroups.map((d) => d.domain)}
        className="w-full"
      >
        {domainGroups.map((domain) => (
          <AccordionItem
            key={domain.domain}
            value={domain.domain}
            className="border rounded-lg mb-2 px-3 data-[state=open]:bg-muted/30"
          >
            <AccordionTrigger className="py-3 hover:no-underline">
              <div className="flex items-center gap-2">
                <span className="font-medium">{domain.domain}</span>
                <Badge variant="outline" className="text-xs h-5 font-normal">
                  {domain.subdomains.reduce(
                    (acc, s) => acc + s.subjects.length,
                    0,
                  )}{' '}
                  resources
                </Badge>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pb-4">
              <div className="space-y-6">
                {domain.subdomains.map((subdomain) => (
                  <div key={subdomain.subdomain}>
                    <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">
                      {subdomain.subdomain}
                    </h4>

                    <div className="overflow-x-auto rounded-lg border bg-card">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-muted/50">
                            <th className="text-left py-2.5 px-3 font-medium text-muted-foreground w-[200px]">
                              Resource
                            </th>
                            {displayActions.map((action) => (
                              <th
                                key={action}
                                className="px-3 py-2.5 text-center font-medium text-muted-foreground w-[80px]"
                              >
                                {ACTION_LABELS[action] ?? action}
                              </th>
                            ))}
                            <th className="w-[60px]"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {subdomain.subjects.map((subject, idx) => {
                            const validActionsForSubject =
                              displayActions.filter((action) =>
                                isActionValidForSubject(action, subject),
                              );
                            const rowEnabled = validActionsForSubject.filter(
                              (action) =>
                                permissionKeys.has(
                                  permissionKey(action, subject),
                                ),
                            ).length;
                            const allEnabled =
                              validActionsForSubject.length > 0 &&
                              rowEnabled === validActionsForSubject.length;

                            return (
                              <tr
                                key={subject}
                                className={cn(
                                  'group transition-colors',
                                  idx !== subdomain.subjects.length - 1 &&
                                    'border-b',
                                  'hover:bg-muted/30',
                                )}
                              >
                                <td className="py-2.5 px-3">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium">
                                      {formatSubject(subject)}
                                    </span>
                                    {rowEnabled > 0 && (
                                      <Badge
                                        variant="secondary"
                                        className="text-[10px] h-4 px-1.5"
                                      >
                                        {rowEnabled}
                                      </Badge>
                                    )}
                                  </div>
                                </td>
                                {displayActions.map((action) => {
                                  const isValidAction = isActionValidForSubject(
                                    action,
                                    subject,
                                  );
                                  const key = permissionKey(action, subject);
                                  const isChecked = permissionKeys.has(key);

                                  if (!isValidAction) {
                                    return (
                                      <td
                                        key={action}
                                        className="px-3 py-2.5 text-center"
                                      >
                                        <div className="flex justify-center">
                                          <span className="text-muted-foreground/30">
                                            —
                                          </span>
                                        </div>
                                      </td>
                                    );
                                  }

                                  const isBlocked = !isAllowableAgentPermission(
                                    action,
                                    subject,
                                  );

                                  return (
                                    <td
                                      key={action}
                                      className="px-3 py-2.5 text-center"
                                    >
                                      <div className="flex justify-center">
                                        <Checkbox
                                          checked={isChecked}
                                          onCheckedChange={() =>
                                            handleToggle(action, subject)
                                          }
                                          disabled={isBlocked}
                                          className={cn(
                                            'transition-all',
                                            isChecked &&
                                              'bg-primary border-primary data-[state=checked]:bg-primary',
                                            isBlocked && 'opacity-50',
                                          )}
                                        />
                                      </div>
                                    </td>
                                  );
                                })}
                                <td className="px-2 py-2.5">
                                  {validActionsForSubject.length > 0 && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 px-2 text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                                      onClick={() =>
                                        handleToggleRow(
                                          subject,
                                          validActionsForSubject,
                                        )
                                      }
                                    >
                                      {allEnabled ? 'None' : 'All'}
                                    </Button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}
